import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, TrendingUp, BookOpen, Users, X } from 'lucide-react';
import { useComponentTranslations, useTranslations } from '../hooks/useTranslations';

const Insights = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const { t, loading: translationsLoading } = useComponentTranslations('insights');
  const { currentLanguage } = useTranslations();

  useEffect(() => {
    fetchInsights();
  }, [currentLanguage]);

  const handleReadMore = useCallback((article) => {
    setSelectedArticle(article);
    setShowModal(true);
  }, []);

  const handleViewAll = useCallback(() => {
    // For now, we'll scroll to the top of the insights section
    // This could be enhanced to show a dedicated insights page
    const insightsSection = document.getElementById('insights');
    if (insightsSection) {
      insightsSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedArticle(null);
  }, []);

  const fetchInsights = async () => {
    try {
      console.log('Starting fetchInsights...');
      setLoading(true);
      setError(null);  // Clear error first
      setInsights([]);  // Clear insights first to force re-render
      
      // Smart backend URL detection for all environments
      const hostname = window.location.hostname;
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost');
      
      let backendUrl;
      
      if (isLocalhost) {
        // Local development - use localhost:8001
        backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      } else if (hostname.includes('dreamline.global')) {
        // Production - use Emergent backend (same origin)
        backendUrl = window.location.origin;
        console.log('Production domain detected, using Emergent backend:', backendUrl);
      } else {
        // Other hosted environments - use same origin
        backendUrl = window.location.origin;
      }
      
      console.log('=== BACKEND URL DETECTION ===');
      console.log('Hostname:', hostname);
      console.log('Is localhost:', isLocalhost);
      console.log('Window origin:', window.location.origin);
      console.log('Env REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
      console.log('Env REACT_APP_PRODUCTION_BACKEND_URL:', process.env.REACT_APP_PRODUCTION_BACKEND_URL);
      console.log('Final backend URL:', backendUrl);
      console.log('Fetching from:', `${backendUrl}/api/insights?language=${currentLanguage}`);
      console.log('=== END DETECTION ===');
      
      const response = await fetch(`${backendUrl}/api/insights?language=${currentLanguage}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      console.log('Response status:', response.status, 'OK:', response.ok);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error text:', errorText);
        throw new Error(`Failed to fetch insights: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Data received:', data.length, 'articles');
      console.log('First article:', data[0]?.title);
      console.log('Setting insights state...');
      
      // Ensure we have valid data
      if (Array.isArray(data) && data.length > 0) {
        setInsights(data);
        setError(null);  // Explicitly clear error
        console.log('Insights state set successfully with', data.length, 'articles');
      } else {
        console.log('No valid articles received');
        setInsights([]);
        setError('No articles available');
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
      console.error('Error stack:', err.stack);
      setError(err.message);
      setInsights([]);
    } finally {
      console.log('Setting loading to false...');
      setLoading(false);
      console.log('fetchInsights completed');
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : currentLanguage, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return dateString;
    }
  };

  const getDefaultImage = (index, category) => {
    // Category-specific images for better relevance
    const categoryImages = {
      'Financial Performance': [
        'https://images.unsplash.com/photo-1707157284454-553ef0a4ed0d', // Financial report with data visualization
        'https://images.unsplash.com/photo-1734503937317-3b88a42ac50c', // Stock market analytics on laptop
        'https://images.pexels.com/photos/7109316/pexels-photo-7109316.jpeg' // Professional working with financial data
      ],
      'Business Strategy': [
        'https://images.unsplash.com/photo-1650978810653-112cb6018092', // Puzzle pieces connecting
        'https://images.unsplash.com/photo-1523875194681-bedd468c58bf', // Chess board strategic thinking
        'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg' // Team collaboration with business documents
      ],
      'Operations Management': [
        'https://images.unsplash.com/photo-1580795479225-c50ab8c3348d', // Operations center with multiple monitors
        'https://images.pexels.com/photos/4481260/pexels-photo-4481260.jpeg', // Warehouse operations management
        'https://images.unsplash.com/photo-1707157284454-553ef0a4ed0d' // Data analysis for operations
      ],
      'Leadership': [
        'https://images.unsplash.com/photo-1584940121819-1883a5d3b0bd', // Professional executive in suit
        'https://images.pexels.com/photos/9034792/pexels-photo-9034792.jpeg', // Diverse business leader
        'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg' // Leadership team meeting
      ]
    };

    // Get category-specific images or fallback to default
    if (categoryImages[category]) {
      return categoryImages[category][index % categoryImages[category].length];
    }

    // Default images for fallback - diverse professional consulting images
    const defaultImages = [
      'https://images.unsplash.com/photo-1707157284454-553ef0a4ed0d',
      'https://images.unsplash.com/photo-1650978810653-112cb6018092',
      'https://images.unsplash.com/photo-1580795479225-c50ab8c3348d',
      'https://images.unsplash.com/photo-1584940121819-1883a5d3b0bd',
      'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg',
      'https://images.pexels.com/photos/7109316/pexels-photo-7109316.jpeg',
      'https://images.pexels.com/photos/4481260/pexels-photo-4481260.jpeg',
      'https://images.pexels.com/photos/9034792/pexels-photo-9034792.jpeg'
    ];
    return defaultImages[index % defaultImages.length];
  };

  if (translationsLoading) {
    return (
      <section id="insights" className="py-20 bg-gradient-to-br from-gray-50 to-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="h-12 bg-gray-200 rounded-lg animate-pulse mb-4"></div>
            <div className="h-6 bg-gray-200 rounded-lg animate-pulse max-w-2xl mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="insights" className="py-20 bg-gradient-to-br from-gray-50 to-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
          >
            {t('title', 'Business Insights & Management Excellence')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('subtitle', 'Expert insights and practical guidance for operational excellence and business optimization')}
          </motion.p>
        </div>

        {(() => {
          if (loading) {
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="h-48 bg-gray-200 animate-pulse"></div>
                    <div className="p-6">
                      <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                      <div className="h-6 bg-gray-200 rounded animate-pulse mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          
          if (error && insights.length === 0) {
            return (
              <div className="text-center py-16">
                <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{t('error', 'Unable to load insights at this time. Please try again later.')}</p>
                <p className="text-xs text-gray-400 mb-4">Debug: loading={String(loading)}, error={String(!!error)}, insights.length={insights.length}</p>
                <button
                  onClick={fetchInsights}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            );
          }
          
          if (insights.length === 0) {
            return (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">{t('loading', 'Loading latest insights...')}</p>
              </div>
            );
          }
          
          return (
          <>
            {/* Featured Article */}
            {insights.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-16"
              >
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="md:flex">
                    <div className="md:w-1/2">
                      <img
                        src={getDefaultImage(0, insights[0].category)}
                        alt={insights[0].title}
                        className="w-full h-64 md:h-full object-cover"
                        loading="eager"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                    </div>
                    <div className="md:w-1/2 p-8 md:p-12">
                      <div className="flex items-center mb-4">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          {insights[0].category}
                        </span>
                        <span className="text-gray-500 text-sm ml-4">
                          {formatDate(insights[0].date)} • {insights[0].read_time}
                        </span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        {insights[0].title}
                      </h3>
                      <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                        {insights[0].excerpt}
                      </p>
                      <button
                        onClick={() => handleReadMore(insights[0])}
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold"
                      >
                        {t('read_more', 'Read More')}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Articles Grid */}
            {insights.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {insights.slice(1).map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  >
                    <img
                      src={getDefaultImage(index + 1, article.category)}
                      alt={article.title}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlPC90ZXh0Pjwvc3ZnPg==';
                      }}
                    />
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                          {article.category}
                        </span>
                        <span className="text-gray-500 text-sm">
                          {article.read_time}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-sm">
                          {formatDate(article.date)}
                        </span>
                        <button
                          onClick={() => handleReadMore(article)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                        >
                          {t('read_more', 'Read More')} →
                        </button>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}

            {/* View All Button */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <button
                onClick={handleViewAll}
                className="bg-gradient-to-r from-evergreen to-dream-teal text-white px-8 py-3 rounded-lg font-semibold hover:from-evergreen-700 hover:to-dream-teal-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                {t('view_all', 'View All Insights')}
              </button>
            </motion.div>
          </>
        )})()}

        {/* Modal */}
        {showModal && selectedArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 md:p-8">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center mb-4">
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                        {selectedArticle.category}
                      </span>
                      <span className="text-gray-500 text-sm ml-4">
                        {formatDate(selectedArticle.date)} • {selectedArticle.read_time}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                      {selectedArticle.title}
                    </h2>
                  </div>
                  <button
                    onClick={closeModal}
                    className="ml-4 text-gray-400 hover:text-gray-600 p-2"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="prose prose-lg max-w-none">
                  {selectedArticle.content ? selectedArticle.content.split('\n').map((paragraph, index) => (
                    <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  )) : (
                    <p className="mb-4 text-gray-700 leading-relaxed">
                      {selectedArticle.excerpt}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Insights;